import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import './panelReport.css';

export interface NavSection {
	id: string;
	label: string;
}

const NUDGE = 160;

// Wider than the chevron, so an auto-scrolled active pill is never left half under it.
const EDGE = 38;

export const prefersReducedMotion = (): boolean =>
	typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const behaviour = (): ScrollBehavior => (prefersReducedMotion() ? 'auto' : 'smooth');

const ReportNav: React.FC<{
	sections: NavSection[];
	activeId: string | null;
	onSelect: (id: string) => void;
	onHeightChange: (height: number) => void;
}> = ({ sections, activeId, onSelect, onHeightChange }) => {
	const navRef = useRef<HTMLElement>(null);
	const scrollerRef = useRef<HTMLDivElement>(null);
	const [overflow, setOverflow] = useState({ left: false, right: false });

	const syncArrows = useCallback(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const max = scroller.scrollWidth - scroller.clientWidth;
		setOverflow({ left: scroller.scrollLeft > 1, right: scroller.scrollLeft < max - 1 });
	}, []);

	useLayoutEffect(() => {
		const nav = navRef.current;
		if (!nav) return undefined;
		const measure = () => {
			onHeightChange(nav.offsetHeight);
			syncArrows();
		};
		measure();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(nav);
		return () => observer.disconnect();
	}, [onHeightChange, syncArrows, sections.length]);

	// A sixteen-section report overflows a 400px column many times over, so the pill the reader
	// is actually on has to be dragged back into the strip.
	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller || !activeId || typeof scroller.scrollTo !== 'function') return;
		const pill = scroller.querySelector<HTMLElement>(`[data-nav-pill="${activeId}"]`);
		if (!pill) return;
		const start = pill.offsetLeft;
		const end = start + pill.offsetWidth;
		// The visible box is the strip MINUS the chevrons, or the pill lands half under one.
		if (start < scroller.scrollLeft + EDGE) {
			scroller.scrollTo({ left: start - EDGE, behavior: behaviour() });
		} else if (end > scroller.scrollLeft + scroller.clientWidth - EDGE) {
			scroller.scrollTo({ left: end - scroller.clientWidth + EDGE, behavior: behaviour() });
		}
	}, [activeId]);

	const nudge = (direction: -1 | 1) => {
		const scroller = scrollerRef.current;
		if (!scroller || typeof scroller.scrollTo !== 'function') return;
		scroller.scrollTo({ left: scroller.scrollLeft + direction * NUDGE, behavior: behaviour() });
	};

	return (
		<nav ref={navRef} className="mg-report-nav" aria-label="Report sections">
			<div ref={scrollerRef} className="mg-report-nav__scroller" onScroll={syncArrows}>
				{sections.map((section) => {
					const active = section.id === activeId;
					return (
						<button
							key={section.id}
							type="button"
							data-nav-pill={section.id}
							aria-current={active ? 'true' : undefined}
							className={`mg-report-nav__pill${active ? ' mg-report-nav__pill--active' : ''}`}
							onClick={() => onSelect(section.id)}
						>
							{section.label}
						</button>
					);
				})}
			</div>
			{overflow.left && (
				<button
					type="button"
					aria-label="Scroll sections left"
					className="mg-report-nav__arrow mg-report-nav__arrow--left"
					onClick={() => nudge(-1)}
				>
					<LeftOutlined />
				</button>
			)}
			{overflow.right && (
				<button
					type="button"
					aria-label="Scroll sections right"
					className="mg-report-nav__arrow mg-report-nav__arrow--right"
					onClick={() => nudge(1)}
				>
					<RightOutlined />
				</button>
			)}
		</nav>
	);
};

export default ReportNav;
